import { useMemo, useRef, useState, useEffect } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Merge } from "lucide-react";

import {
  timeToMinutes,
  minutesToTime,
  dayNames,
  dayShort,
  type QuickForm,
  type EditForm,
} from "./pricingGridUtils";
import { usePricingDrag } from "./PricingDragHandlers";
import PricingGridRow from "./PricingGridRow";
import PricingEditPopover from "./PricingEditPopover";
import PricingQuickCreatePopover from "./PricingQuickCreatePopover";

interface PricingRule {
  id: string;
  name: string | null;
  day_of_week: number[] | null;
  start_time: string;
  end_time: string;
  price_per_hour: number;
  is_peak: boolean;
  [key: string]: unknown;
}

interface Props {
  rules: PricingRule[];
  hallHours: { start: number; end: number };
  timeStepMinutes?: number;
  onClickRule: (rule: PricingRule) => void;
  onClickEmpty: (day: number, time: string) => void;
  onResizeRule?: (ruleId: string, newStart: string, newEnd: string) => void;
  onMoveRule?: (ruleId: string, data: { day_of_week: number[]; start_time: string; end_time: string }) => void;
  onMergeRules?: (ruleIds: string[], keepRuleId: string) => void;
  onQuickCreate?: (data: {
    day_of_week: number[];
    start_time: string;
    end_time: string;
    price_per_hour: number;
    is_peak: boolean;
    name: string;
  }) => void;
  onUpdateRule?: (ruleId: string, data: { price_per_hour: number; is_peak: boolean; start_time: string; end_time: string; name: string; day_of_week: number[] }) => void;
  onDeleteRule?: (ruleId: string) => void;
}

export default function PricingWeekGrid({
  rules, hallHours, timeStepMinutes = 30, onClickRule, onClickEmpty,
  onResizeRule, onMoveRule, onMergeRules, onQuickCreate, onUpdateRule, onDeleteRule,
}: Props) {
  const CELL_MINUTES = timeStepMinutes;
  const startMinutes = hallHours.start * 60;
  const endMinutes = hallHours.end * 60;
  const totalSlots = (endMinutes - startMinutes) / CELL_MINUTES;
  const gridRef = useRef<HTMLDivElement>(null);

  const [quickForm, setQuickForm] = useState<QuickForm | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editAnchor, setEditAnchor] = useState<{ top: number; left: number } | null>(null);
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set());

  const {
    _drag, setDrag, isDragging,
    isSelected,
    handleDragStart, handleDragEnter, handleDragEnd,
    resizePreview, handleResizeDragStart,
    moveDrag, isMoving, handleMoveDragStart,
    overlapWarning,
  } = usePricingDrag(gridRef, rules, startMinutes, endMinutes, CELL_MINUTES, onResizeRule, onMoveRule);

  // Cell-rule map with resize preview
  const cellRuleMap = useMemo(() => {
    const map: Record<string, PricingRule | null> = {};
    const effectiveRules = resizePreview
      ? rules.map((r) =>
          r.id === resizePreview.ruleId
            ? { ...r, start_time: resizePreview.start, end_time: resizePreview.end }
            : r
        )
      : rules;
    for (let day = 1; day <= 7; day++) {
      for (let slot = 0; slot < totalSlots; slot++) {
        const slotMinute = startMinutes + slot * CELL_MINUTES;
        const slotTime = minutesToTime(slotMinute);
        const matching = effectiveRules.filter((r) => {
          if (r.day_of_week && r.day_of_week.length > 0 && !r.day_of_week.includes(day)) return false;
          const rStart = r.start_time?.slice(0, 5);
          const rEnd = r.end_time?.slice(0, 5);
          return slotTime >= rStart && slotTime < rEnd;
        });
        const rule = matching.find((r) => r.is_peak) ?? matching[0] ?? null;
        map[`${day}-${slot}`] = rule;
      }
    }
    return map;
  }, [rules, resizePreview, totalSlots, startMinutes, CELL_MINUTES]);

  // Day rule spans for block overlays
  const dayRuleSpans = useMemo(() => {
    const result: Record<number, { rule: PricingRule; startSlot: number; endSlot: number }[]> = {};
    for (let day = 1; day <= 7; day++) {
      const spans: { rule: PricingRule; startSlot: number; endSlot: number }[] = [];
      const dayRules = rules.filter((r) => {
        if (!r.day_of_week || r.day_of_week.length === 0) return true;
        return r.day_of_week.includes(day);
      });
      for (const rule of dayRules) {
        const rStart = timeToMinutes(rule.start_time);
        const rEnd = timeToMinutes(rule.end_time);
        const sSlot = Math.max(0, Math.floor((rStart - startMinutes) / CELL_MINUTES));
        const eSlot = Math.min(totalSlots, Math.ceil((rEnd - startMinutes) / CELL_MINUTES));
        if (eSlot > sSlot) {
          spans.push({ rule, startSlot: sSlot, endSlot: eSlot });
        }
      }
      result[day] = spans;
    }
    return result;
  }, [rules, totalSlots, startMinutes, CELL_MINUTES]);

  // Mouse up handler for drag selection
  const handleMouseUp = () => {
    const sel = handleDragEnd();
    if (!sel) return;

    const days = [];
    for (let d = sel.minDay; d <= sel.maxDay; d++) days.push(d);
    const startTime = minutesToTime(startMinutes + sel.minSlot * CELL_MINUTES);
    const endTime = minutesToTime(startMinutes + (sel.maxSlot + 1) * CELL_MINUTES);

    if (onQuickCreate) {
      setQuickForm({ days, startTime, endTime, price: 0, isPeak: false, name: "" });
      setPopoverOpen(true);
    } else {
      onClickEmpty(days[0], startTime);
      setDrag(null);
    }
  };

  const handleMouseDown = (day: number, slot: number, e: React.MouseEvent) => {
    handleDragStart(day, slot, e);
    setPopoverOpen(false);
    setQuickForm(null);
    setEditForm(null);
    setSelectedRuleIds(new Set());
  };

  // Quick create save
  const handleQuickSave = () => {
    if (!quickForm || !onQuickCreate) return;
    onQuickCreate({
      day_of_week: quickForm.days,
      start_time: quickForm.startTime,
      end_time: quickForm.endTime,
      price_per_hour: quickForm.price,
      is_peak: quickForm.isPeak,
      name: quickForm.name || (quickForm.isPeak ? "Peak" : "Off-peak"),
    });
    setPopoverOpen(false);
    setQuickForm(null);
    setDrag(null);
  };

  const cancelSelection = () => {
    setPopoverOpen(false);
    setQuickForm(null);
    setDrag(null);
  };

  // Rule block click (inline edit or shift-select for merge)
  const handleRuleBlockClick = (e: React.MouseEvent, rule: PricingRule) => {
    e.stopPropagation();

    if (e.shiftKey && onMergeRules) {
      setSelectedRuleIds((prev) => {
        const next = new Set(prev);
        if (next.has(rule.id)) next.delete(rule.id);
        else next.add(rule.id);
        return next;
      });
      return;
    }

    if (onUpdateRule) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const popoverHeight = 380;
      const popoverWidth = 256;
      const margin = 8;

      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < popoverHeight + margin;

      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - margin) {
        left = window.innerWidth - popoverWidth - margin;
      }

      setEditAnchor({
        top: openUpward ? rect.top - popoverHeight - margin : rect.bottom + margin,
        left: Math.max(margin, left),
      });
      setEditForm({
        ruleId: rule.id,
        name: rule.name ?? "",
        price: rule.price_per_hour,
        isPeak: rule.is_peak,
        startTime: rule.start_time?.slice(0, 5),
        endTime: rule.end_time?.slice(0, 5),
        days: rule.day_of_week ?? [],
      });
      setSelectedRuleIds(new Set());
    } else {
      onClickRule(rule);
    }
  };

  const handleEditSave = () => {
    if (!editForm || !onUpdateRule) return;
    onUpdateRule(editForm.ruleId, {
      price_per_hour: editForm.price,
      is_peak: editForm.isPeak,
      start_time: editForm.startTime,
      end_time: editForm.endTime,
      name: editForm.name,
      day_of_week: editForm.days,
    });
    setEditForm(null);
  };

  const handleEditDelete = () => {
    if (!editForm || !onDeleteRule) return;
    onDeleteRule(editForm.ruleId);
    setEditForm(null);
  };

  // Close edit popover on scroll
  useEffect(() => {
    if (!editForm) return;
    const close = () => setEditForm(null);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [editForm]);

  // Merge logic
  const canMerge = useMemo(() => {
    if (selectedRuleIds.size < 2) return false;
    const selected = rules.filter((r) => selectedRuleIds.has(r.id));
    const dayKey = JSON.stringify(selected[0]?.day_of_week?.sort());
    return selected.every((r) => JSON.stringify(r.day_of_week?.sort()) === dayKey);
  }, [selectedRuleIds, rules]);

  const handleMerge = () => {
    if (!onMergeRules || selectedRuleIds.size < 2) return;
    const ids = Array.from(selectedRuleIds);
    const selected = rules.filter((r) => ids.includes(r.id));
    selected.sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
    onMergeRules(ids, selected[0].id);
    setSelectedRuleIds(new Set());
  };

  return (
    <ScrollArea className="w-full">
      <div
        className="min-w-[700px] select-none relative"
        ref={gridRef}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { if (isDragging) handleMouseUp(); }}
      >
        {/* Merge toolbar */}
        {selectedRuleIds.size > 0 && (
          <div className="sticky top-0 z-40 flex items-center gap-2 bg-muted/90 backdrop-blur-sm rounded-md px-3 py-1.5 mb-1 border">
            <span className="text-xs text-muted-foreground">{selectedRuleIds.size} block markerade</span>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={!canMerge} onClick={handleMerge}>
              <Merge className="h-3 w-3" />
              Sammanfoga
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedRuleIds(new Set())}>
              Avmarkera
            </Button>
          </div>
        )}

        {/* Overlap warning */}
        {overlapWarning && (
          <div className="sticky top-0 z-40 bg-destructive/10 text-destructive border border-destructive/30 rounded-md px-3 py-1.5 mb-1 text-xs">
            ⚠️ Överlappning med &quot;{overlapWarning}&quot;
          </div>
        )}

        {/* Hour headers */}
        <div className="flex">
          <div className="w-16 shrink-0" />
          <div className="flex-1 flex">
            {Array.from({ length: totalSlots }).map((_, slot) => {
              const minute = startMinutes + slot * CELL_MINUTES;
              const isHour = minute % 60 === 0;
              return (
                <div key={slot} className="flex-1 text-center">
                  {isHour && (
                    <span className="text-[10px] text-muted-foreground">
                      {minutesToTime(minute)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Day rows */}
        {dayNames.map((_name, i) => {
          const isoDay = i + 1;
          return (
            <PricingGridRow
              key={isoDay}
              dayIndex={i}
              dayShortName={dayShort[i]}
              totalSlots={totalSlots}
              startMinutes={startMinutes}
              cellMinutes={CELL_MINUTES}
              cellRuleMap={cellRuleMap}
              spans={dayRuleSpans[isoDay] || []}
              selectedRuleIds={selectedRuleIds}
              editingRuleId={editForm?.ruleId}
              isSelected={isSelected}
              onMouseDown={handleMouseDown}
              onMouseEnter={(day, slot) => handleDragEnter(day, slot)}
              onRuleBlockClick={handleRuleBlockClick}
              onResizeDragStart={handleResizeDragStart}
              onMoveDragStart={handleMoveDragStart}
              moveDrag={moveDrag}
              isMoving={isMoving}
              overlapWarning={overlapWarning}
              canMove={!!onMoveRule}
            />
          );
        })}
        <div className="border-t" />

        {/* Inline edit popover */}
        {editForm && editAnchor && (
          <PricingEditPopover
            editForm={editForm}
            anchor={editAnchor}
            onUpdate={setEditForm}
            onSave={handleEditSave}
            onDelete={handleEditDelete}
            onClose={() => setEditForm(null)}
          />
        )}
      </div>

      <ScrollBar orientation="horizontal" />

      {/* Quick-create popover */}
      {popoverOpen && quickForm && (
        <PricingQuickCreatePopover
          quickForm={quickForm}
          onUpdate={setQuickForm}
          onSave={handleQuickSave}
          onCancel={cancelSelection}
        />
      )}
    </ScrollArea>
  );
}
